import dayjs from 'dayjs';
import is from 'is_js';
import {extend} from 'lodash';
import {toClass} from './modules/stringToSelector';
import CreateEvent from './modules/create-event';
import './modules/polyfill-classlist-ie';

const now = new Date();

class Calendar {
  constructor(el, opts) {
    this.el = el;
    this.children = opts.children || '.calendar';
    this.mode = opts.mode || 'calendar';
    this.navigation = extend({
      nextEl: '.calendar-button-next',
      prevEl: '.calendar-button-prev',
      nextText: 'Next',
      prevText: 'Prev',
    }, opts.navigation)
    this.startDay = opts.startDay || 0; // 0 is Sunday
    this.minDate = opts.minDate ? new Date(opts.minDate) : null;
    this.maxDate = opts.maxDate ? new Date(opts.maxDate) : null;
    this.numberOfMonths = opts.numberOfMonths || 2;
    this.weekDayName = opts.weekDayName || ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    this.monthName = opts.monthName || [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    this.buddhist = opts.buddhist || false;
    this.className = extend({
      isCheckin: 'is-checkin',
      isCheckout: 'is-checkout',
      isSelected: 'is-selected',
      isSelecting: 'is-selecting',
      isRanged: 'is-ranged',
      isDisabled: 'is-disabled',
      isDisabledDate: 'is-disabled-date',
      isFuture: 'is-future',
      isToday: 'is-today',
      isPast: 'is-past',
      isWeekend: 'is-weekend',
      isWeekday: 'is-weekday',
      isLastMonth: 'is-lastmonth',
      isNextMonth: 'is-nextmonth',
      isHover: 'is-hover',
      isHold: 'is-hold',

      _container: 'calendar-picker',
      _table: 'calendar-picker__table',
      _head: 'calendar-picker__head',
      _body: 'calendar-picker__body',
      _title: 'calendar-picker__title',
      _weekday: 'weekday',
      _day: 'cell'
    }, opts.className);
    this.disableDate = opts.disableDate || [];
    this.disableDateClickable = opts.disableDateClickable || false;
    this.current = {
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
      range: new Array(2),
      state: 'select_range'
    };
    this.showOtherMonth = opts.showOtherMonth || false;
    this.rtl = opts.rtl || false;
    this.on = extend({
      init: null,
      renderDay: null
    }, opts.on);

    this._init = false;
    this._registerState = [
      'select_checkin',
      'select_checkin_only',
      'select_checkout',
      'select_checkout_only',
      'select_range',
      'ready'
    ];
  }

  renderWeekDay() {
    var weekDay = document.createElement('tr');
    for (var i = 0 + this.startDay; i <= 6 + this.startDay; i++) {
      let day = document.createElement('th');
      day.classList.add(this.className._weekday);

      is.inArray(i, [0, 6]) ? day.classList.add(this.className.isWeekend) : day.classList.add(this.className.isWeekday);

      day.innerHTML = this.weekDayName[i % 7];
      weekDay.appendChild(day);
    }
    return weekDay;
  }

  renderDay(date, base = new Date(this.current.year, this.current.month), forceDisabled = false) {
    let d = new Date(date);
    let f = dayjs(d).format('YYYY-MM-DD');
    let day = document.createElement('td');
    let isDisableDate = (forceClickable) => {
      let minDate = dayjs(d).isBefore(this.minDate, 'd');
      let maxDate = dayjs(d).isAfter(this.maxDate, 'd');
      let inDisabledDateList = !forceClickable && is.inArray(f, this.disableDate);
      let lastMonth = dayjs(d).isBefore(base, 'month');
      let nextMonth = dayjs(d).isAfter(base, 'month');
      let rangeDate = () => {
        switch (this.state) {
          case 'select_checkin_only':
            return dayjs(d).isAfter(this.checkout, 'd');
          case 'select_checkout_only':
            return dayjs(d).isBefore(this.checkin, 'd');
          default:
            return false;
        }
      };
      return is.any.truthy(forceDisabled, minDate, maxDate, inDisabledDateList, lastMonth, nextMonth, rangeDate());
    };
    let dayClassList = classstr => {
      let classes = [classstr];
      if (is.today(d)) classes.push(this.className.isToday);
      if (is.past(d) && is.not.today(d)) classes.push(this.className.isPast);
      if (is.future(d) && is.not.today(d)) classes.push(this.className.isFuture);
      if (is.weekday(d)) classes.push(this.className.isWeekday);
      if (is.weekend(d)) classes.push(this.className.isWeekend);
      if (dayjs(d).isBefore(base, 'month')) classes.push(this.className.isLastMonth);
      if (dayjs(d).isAfter(base, 'month')) classes.push(this.className.isNextMonth);
      if (isDisableDate()) classes.push(this.className.isDisabled);
      if (is.inArray(f, this.disableDate)) classes.push(this.className.isDisabledDate);
      return classes.join(' ').trim();
    };
    
    let button = `<button
      data-year="${d.getFullYear()}"
      data-month="${d.getMonth()}"
      data-date="${d.getDate()}"
      data-time="${d.getTime()}"
      data-date-format="${f}"
      class="btn-day"
      type="button"
      ${isDisableDate(this.disableDateClickable) ? 'disabled' : ''}
    >
      <span class="date">${d.getDate()}</span>
    </button>`;
    day.className = dayClassList(this.className._day);
    day.innerHTML = button;

    CreateEvent('renderDay', {
      el: day,
      d: d,
      year: d.getFullYear(),
      month: d.getMonth(),
      date: d.getDate(),
      day: d.getDay(),
    }, document.querySelector(this.el))

    if ('function' === typeof this.on.renderDay) {
      this.on.renderDay( {
        el: day,
        d: d,
        year: d.getFullYear(),
        month: d.getMonth(),
        date: d.getDate(),
        day: d.getDay(),
      } )
    }

    return day;
  }

  renderMonth(date) {
    let tbody = document.createElement('tbody');
    tbody.classList.add(this.className._body);
    let d = new Date(date);
    let firstDate = new Date(d.getFullYear(), d.getMonth());
    let diff = () => {
      var val = firstDate.getDay() - this.startDay;
      val = val < 0 ? val + 7 : val;
      return val;
    };
    let firstOfWeek = dayjs(firstDate)
      .subtract(diff(), 'd')
      .toDate();
    let fakebtn = () => {
      let fake = document.createElement('td');
      fake.classList.add(this.className._day, 'is-fake', this.className.isDisabled);
      return fake;
    }

    for (var w = 0; w < 6; w++) {
      let week = document.createElement('tr');
      let first = dayjs(firstOfWeek)
        .add(7 * w, 'd')
        .toDate();

      for (var i = 0; i <= 6; i++) {
        let f = dayjs(first)
          .add(i, 'd')
          .toDate();

        if (!this.showOtherMonth) {
          if (!dayjs(f).isSame(d, 'month')) {
            week.appendChild(fakebtn());
          } else {
            week.appendChild(this.renderDay(f, d));
          }
        } else {
          week.appendChild(this.renderDay(f, d));
        }
      }

      tbody.appendChild(week);
    }

    return tbody;
  }

  renderCalendar(date) {
    let d = new Date(date);
    let table = document.createElement('table');
    let thead = document.createElement('thead');
    table.classList.add(this.className._table)
    thead.classList.add(this.className._head)
    let theYear = this.buddhist ? d.getFullYear() + 543 : d.getFullYear();
    let title = `<tr><th class="${this.className._title}" colspan="7">
      <span class="month">${this.monthName[d.getMonth()]}</span>
      <span class="year">${theYear}</span>
    </th></tr>`;

    thead.insertAdjacentHTML('beforeend', title);
    thead.appendChild(this.renderWeekDay());
    table.appendChild(thead);
    table.appendChild(this.renderMonth(date));

    return table;
  }

  renderRange(a = this.checkin, b = this.checkout) {    
    let el = document.querySelector(this.el);
    let ch = [...el.querySelectorAll('[data-date]')];
    let getSelectedDate = date => {
      return [...el.querySelectorAll(`[data-year="${date.getFullYear()}"][data-month="${date.getMonth()}"][data-date="${date.getDate()}"]`)]
    }
    let start = is.date(a) && getSelectedDate(a)
    let end = is.date(b) && getSelectedDate(b)

    if (start) {
      let ci = [...el.querySelectorAll(toClass(this.className.isCheckin))]
      for (let i = 0; i < ci.length; i++) {
        let node = ci[i];
        node.classList.remove(this.className.isCheckin)
      }
      let e = getSelectedDate(a)
      for (let i = 0; i < e.length; i++) {
        let node = e[i]
        node.parentNode.classList.add(this.className.isSelected, this.className.isCheckin)
        node.parentNode.classList.remove(this.className.isCheckout)
      }
    } else {
      let e = [...el.querySelectorAll(toClass(this.className.isCheckin))]
      for (let i = 0; i < e.length; i++) {
        let node = e[i]
        node.classList.remove(this.className.isCheckin)
      }
    }

    if (end) {
      let co = [...el.querySelectorAll(toClass(this.className.isCheckout))]
      for (let i = 0; i < co.length; i++) {
        let node = co[i];
        node.classList.remove(this.className.isCheckout)
      }
      let e = getSelectedDate(b)
      for (let i = 0; i < e.length; i++) {
        let node = e[i].parentNode
        node.classList.add(this.className.isSelected, this.className.isCheckout)
        node.classList.remove(this.className.isCheckin)
      }
    } else {
      let e = [...el.querySelectorAll(toClass(this.className.isCheckout))]
      for (let i = 0; i < e.length; i++) {
        let node = e[i]
        node.classList.remove(this.className.isCheckout)
      }
    }

    if (!start || !end) {
      for(let i = 0; i < ch.length; i++) {
        let node = ch[i].parentNode
        let excerpt = node.classList.contains(this.className.isCheckin, this.className.isCheckout)
        if (!excerpt) {
          node.classList.remove(this.className.isSelected)
        }
      }
    } else {
      let between = ch.filter(e => {
        let d = new Date(e.getAttribute('data-date-format'))
        return dayjs(d).isAfter(a, 'date') && dayjs(d).isBefore(b, 'date')
      })
      let around = ch.filter(e => {
        let d = new Date(e.getAttribute('data-date-format'))
        return dayjs(d).isBefore(a, 'date') || dayjs(d).isAfter(b, 'date')
      })
      for (let i = 0; i < between.length; i++) {
        let node = between[i].parentNode;
        node.classList.add(this.className.isSelected)
        node.classList.remove(this.className.isCheckin, this.className.isCheckout)
      }
      for (let i = 0; i < around.length; i++) {
        let node = around[i].parentNode;
        node.classList.remove(this.className.isSelected, this.className.isCheckin, this.className.isCheckout)
      }
    }

    CreateEvent('renderRange', {
      checkin: this.checkin,
      checkout: this.checkout,
      current: this.current
    }, el);
  }

  renderNavigation() {
    let cont = document.querySelector(this.el)
    let next = cont.querySelector(this.navigation.nextEl)
    let prev = cont.querySelector(this.navigation.prevEl)

    if (next) {
      next.setAttribute('role', 'button')
      next.setAttribute('aria-label', 'Next Month')
      next.setAttribute('tabindex', 0)
      next.innerHTML = this.navigation.nextText
      next.addEventListener('click', () => {
        this.next()
        next.classList.toggle(this.className.isDisabled, !this.canNextPrev().canNext)
        if (prev) prev.classList.toggle(this.className.isDisabled, !this.canNextPrev().canPrev)
      })
      next.addEventListener('keypress', e => {
        if ( [13,32].some(a => a === e.keyCode) ) next.click()
      })
    }

    if (prev) {
      prev.setAttribute('role', 'button')
      prev.setAttribute('aria-label', 'Previous Month')
      prev.setAttribute('tabindex', 0)
      prev.innerHTML = this.navigation.prevText
      prev.addEventListener('click', () => {
        this.prev()
        prev.classList.toggle(this.className.isDisabled, !this.canNextPrev().canPrev)
        if (next) next.classList.toggle(this.className.isDisabled, !this.canNextPrev().canNext)
      })
      prev.addEventListener('keypress', e => {
        if ( [13,32].some(a => a === e.keyCode) ) prev.click()
      })
    }

    cont.addEventListener('render', ev => {
      let {canNextPrev} = ev.detail
      if (next) next.classList.toggle(this.className.isDisabled, !canNextPrev.canNext)
      if (prev) prev.classList.toggle(this.className.isDisabled, !canNextPrev.canPrev)
    })
  }

  render(date = new Date(this.current.year, this.current.month)) {
    var d = new Date(date);
    var el = document.querySelector(this.el);
    var ch = [...el.querySelectorAll(this.children)];

    for (let i = 0; i < ch.length; i++) {
      let el = ch[i]
      el.innerHTML = '';
      el.appendChild(this.renderCalendar(new Date(d.getFullYear(), d.getMonth() + i)));
    }

    this.renderRange();
    this.bindEvents();
    this.current.year = d.getFullYear();
    this.current.month = d.getMonth();

    CreateEvent('render', {
      checkin: this.checkin,
      checkout: this.checkout,
      current: this.current,
      canNextPrev: this.canNextPrev()
    }, el)
  }

  init(force) {
    if (this._init && force !== true) return;

    let container = document.querySelector(this.el)
    if (!container) return;

    container.classList.add(this.className._container)
    if (this.rtl) {
      container.setAttribute('dir', 'rtl')
    }

    Boolean(this.checkin) && Boolean(this.checkout)
      ? this.state = 'ready'
      : this.state = 'select_range'

    this.renderNavigation();
    this.render();

    this._init = true;

    if ('function' === typeof this.on.init) {
      this.on.init(this);
    }
  }

  bindEvents() {
    let calendarRules = require('./rules/touch-device').default;
    let datepickerRules = require('./rules/datepicker').default;
    switch(this.mode) {
      case 'datepicker': datepickerRules(this); break;
      default: calendarRules(this);
    }
  }

  inView(date) {
    if (!date || !Date.parse(date)) return;
    let d = new Date(date)
    let l = [...document.querySelector(this.el).querySelectorAll(this.children)].length
    let a = new Date(this.current.year, this.current.month, 1)
    let b = new Date(this.current.year, this.current.month + l, 0)
    return !dayjs(d).isBefore(a, 'date') && !dayjs(d).isAfter(b, 'date');
  }

  goto(year, month, offset = 0) {
    let d = new Date(year, month - offset);
    this.render(d);
  }

  canNextPrev() {
    let currentYear = this.current.year;
    let currentMonth = this.current.month;
    let offset = [...document.querySelector(this.el).querySelectorAll(this.children)].length - 1;
    let isCanNext = true;
    let isCanPrev = true;

    if ( dayjs(new Date(currentYear, currentMonth + offset + 1)).isAfter(this.maxDate, 'month') ) {
      isCanNext = false
    }

    if ( dayjs(new Date(currentYear, currentMonth - 1)).isBefore(this.minDate, 'month') ) {
      isCanPrev = false
    }

    return {
      canNext: isCanNext,
      canPrev: isCanPrev
    }
  }

  next() {
    let currentYear = this.current.year;
    let currentMonth = this.current.month;
    if (this.canNextPrev().canNext) {
      this.goto(currentYear, currentMonth + 1);
    }
    return {
      canPrev: this.canNextPrev().canPrev,
      canNext: this.canNextPrev().canNext
    }
  }

  prev() {
    let currentYear = this.current.year;
    let currentMonth = this.current.month;
    if (this.canNextPrev().canPrev) {
      this.goto(currentYear, currentMonth - 1);
    }
    return {
      canPrev: this.canNextPrev().canPrev,
      canNext: this.canNextPrev().canNext
    }
  }

  today() {
    this.goto(now.getFullYear(), now.getMonth());
  }

  changeCheckin() {
    if (this.checkin && this.checkout) {
      this.state = 'select_checkin_only';
      this.render();
    }
  }

  changeCheckout() {
    if (this.checkin && this.checkout) {
      this.state = 'select_checkout_only';
      this.render();
    }
  }

  clearCheckin() {
    this.checkin = undefined;
  }

  clearCheckout() {
    this.checkout = undefined;
  }

  clear() {
    this.clearCheckin();
    this.clearCheckout();
    this.renderRange();
    this.state = 'select_range';
    CreateEvent('clear', {}, document.querySelector(this.el));
  }

  getRange() {
    return [this.checkin, this.checkout]
  }

  setRange(a, b) {
    let range = [a, b].sort((a, b) => new Date(a) - new Date(b));
    this.checkin = range[0];
    this.checkout = range[1];
    if (this.inView(a) && this.inView(b)) {
      this.renderRange(range[0], range[1]);
    } else {
      this.goto(range[0].getFullYear(), range[0].getMonth());
    }
    this.state = 'ready';

    CreateEvent( 'setRange', {
      ranges: this.getRange(),
      checkin: this.checkin,
      checkout: this.checkout
    }, document.querySelector(this.el) );
  }

  get checkin() {
    return this.current.range[0];
  }

  set checkin(date) {
    if (Date.parse(date)) {
      let d = new Date(date);
      d.setHours(0);
      d.setMinutes(0);
      d.setSeconds(0);
      d.setMilliseconds(0);
      this.current.range[0] = d;
  
      CreateEvent( 'setCheckin', {
        dateobj: d,
        year: d.getFullYear(),
        month: d.getMonth(),
        date: d.getDate(),
        day: d.getDay()
      }, document.querySelector(this.el) )
    } else {
      this.current.range[0] = date
    }
  }

  get checkout() {
    return this.current.range[1];
  }

  set checkout(date) {
    if (Date.parse(date)) {
      let d = new Date(date);
      d.setHours(0);
      d.setMinutes(0);
      d.setSeconds(0);
      d.setMilliseconds(0);
      this.current.range[1] = d;
  
      CreateEvent( 'setCheckout', {
        dateobj: d,
        year: d.getFullYear(),
        month: d.getMonth(),
        date: d.getDate(),
        day: d.getDay()
      }, document.querySelector(this.el) );
    } else {
      this.current.range[1] = date
    }
  }

  get state() {
    return this.current.state;
  }

  set state(statename) {
    if (is.inArray(statename, this._registerState)) {
      this.current.state = statename;
      CreateEvent('stateChange', statename, document.querySelector(this.el))
    } else {
      console.error('state is not matches by registered');
    }
  }
}

export {
  Calendar
};